/**
 * Cookie Consent Storage Module
 *
 * Handles reading/writing consent state to a first-party cookie.
 * Cookie specs:
 * - Name: optioneer_consent
 * - Path: /
 * - SameSite: Lax
 * - Secure: true in production
 * - Max-Age: 365 days
 */

import type { ConsentState, ConsentCategory, ConsentCategories } from './types'

/** Current consent version - increment when categories change to re-prompt users */
export const CONSENT_VERSION = '1'

/** Cookie name for consent storage */
export const COOKIE_NAME = 'optioneer_consent'

/** Cookie max age in seconds (365 days) */
const COOKIE_MAX_AGE = 365 * 24 * 60 * 60

/**
 * Check if we're running in a secure context (HTTPS)
 */
function isSecureContext(): boolean {
  if (typeof window === 'undefined') return false
  return window.location.protocol === 'https:'
}

/**
 * Get consent state from cookie
 * @returns ConsentState if valid consent exists, null otherwise
 */
export function getConsent(): ConsentState | null {
  if (typeof document === 'undefined') return null

  try {
    const cookies = document.cookie.split(';')
    const consentCookie = cookies.find((c) => c.trim().startsWith(`${COOKIE_NAME}=`))

    if (!consentCookie) return null

    const value = consentCookie.split('=')[1]
    if (!value) return null

    const decoded = decodeURIComponent(value)
    const consent: ConsentState = JSON.parse(decoded)

    // Validate version - if mismatch, treat as no consent
    if (consent.version !== CONSENT_VERSION) {
      return null
    }

    // Validate required fields
    if (
      typeof consent.timestamp !== 'number' ||
      typeof consent.categories !== 'object' ||
      consent.categories.necessary !== true
    ) {
      return null
    }

    return consent
  } catch {
    return null
  }
}

/**
 * Save consent state to cookie
 */
export function setConsent(consent: ConsentState): void {
  if (typeof document === 'undefined') return

  const value = encodeURIComponent(JSON.stringify(consent))

  let cookieString = `${COOKIE_NAME}=${value}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax`

  // Add Secure flag in production (HTTPS)
  if (isSecureContext()) {
    cookieString += '; Secure'
  }

  document.cookie = cookieString
}

/**
 * Delete consent cookie (for reset/testing)
 */
export function resetConsent(): void {
  if (typeof document === 'undefined') return

  // Set expired cookie to delete
  document.cookie = `${COOKIE_NAME}=; path=/; max-age=0; SameSite=Lax`
}

/**
 * Check if user has granted consent for a specific category
 */
export function hasConsent(category: ConsentCategory): boolean {
  const consent = getConsent()
  if (!consent) return category === 'necessary' // Necessary is always allowed
  return consent.categories[category] === true
}

/**
 * Create a consent state object
 */
export function createConsentState(
  categories: ConsentCategories,
  choice: 'all' | 'none' | 'custom'
): ConsentState {
  return {
    version: CONSENT_VERSION,
    timestamp: Date.now(),
    categories,
    choice,
  }
}

/**
 * Create "accept all" consent state
 */
export function createAcceptAllConsent(): ConsentState {
  return createConsentState(
    {
      necessary: true,
      functional: true,
      analytics: true,
      marketing: true,
    },
    'all'
  )
}

/**
 * Create "reject all" consent state (only necessary allowed)
 */
export function createRejectAllConsent(): ConsentState {
  return createConsentState(
    {
      necessary: true,
      functional: false,
      analytics: false,
      marketing: false,
    },
    'none'
  )
}
